import crypto from 'crypto';
import React, { useCallback } from 'react';
import sorterDown from '../../assets/icons/sorter-down.svg';
import sorterUp from '../../assets/icons/sorter-up.svg';
import sorterNone from '../../assets/icons/sorter.svg';
import notFound from '../../assets/images/not-found.png';
import { useTheme } from '../../hooks/theme';
import { SpinnerLong } from '../../icons';

import Pagination from './Pagination';
import {
  Container,
  LoadingBackground,
  LoadingContainer,
  NoDataContainer,
  TableBody,
  TableContainer,
  TableDataCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from './Table.styles';
import { Column, TableProps } from './Table.types';

export const Table: React.FC<TableProps> = ({
  style,
  rows,
  columns,
  sorting,
  pagination,
  loading,
}) => {
  const theme = useTheme();

  const handleSortClick = useCallback(
    (column: Column) => {
      const { sorter, updateSorter } = sorting;

      if (!sorter[column.accessor]) {
        return;
      }

      updateSorter(oldSorter =>
        Object.fromEntries(
          Object.keys(oldSorter).map(key => {
            if (key === column.accessor) {
              if (sorter[column.accessor] === 'asc') {
                return [key, 'desc'];
              }
              if (sorter[column.accessor] === 'desc') {
                return [key, 'none'];
              }
              return [key, 'asc'];
            }
            return [key, 'none'];
          }),
        ),
      );
    },
    [sorting],
  );

  const rowId = useCallback(
    (row: unknown) => crypto.createHash('md5').update(JSON.stringify(row)).digest('hex'),
    [],
  );

  return (
    <Container>
      {loading && <LoadingBackground data-testid="loading-background" />}
      {loading && (
        <LoadingContainer>
          <SpinnerLong color={theme.colors.brand.primary[100]} size={40} />
        </LoadingContainer>
      )}
      <TableContainer data-testid="table" style={style}>
        <TableHeader>
          <TableHeaderRow>
            {columns.map(column => (
              <TableHeaderCell
                key={column.accessor}
                onClick={() => handleSortClick(column)}
                style={{ width: column.rowSize }}
              >
                <div>
                  {column.header}
                  <div style={{ width: '10px' }} />
                  {sorting?.sorter[column.accessor] && (
                    <>
                      {sorting.sorter[column.accessor] === 'asc' ? (
                        <img src={sorterUp} alt="Ordem ascendente" />
                      ) : sorting.sorter[column.accessor] === 'desc' ? (
                        <img src={sorterDown} alt="Ordem descendente" />
                      ) : (
                        <img src={sorterNone} alt="Sem ordem" />
                      )}
                    </>
                  )}
                </div>
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </TableHeader>
        {rows?.length > 0 && (
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id ?? rowId(row)}>
                {columns.map(column => (
                  <TableDataCell key={column.accessor}>
                    {column.format
                      ? column.format(row[column.accessor], row)
                      : row[column.accessor]}
                  </TableDataCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        )}
      </TableContainer>
      {rows.length === 0 && (
        <NoDataContainer data-testid="no-data-container">
          <img src={notFound} alt="Not found" />
          <h4>Ops, não encontramos nenhum resultado para sua pesquisa</h4>
          <p>Talvez seja necessário ajustar os filtros.</p>
        </NoDataContainer>
      )}
      {pagination && <Pagination {...pagination} />}
    </Container>
  );
};
